import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components/macro';
import { NavigationLink } from '../../navigation/navigationLink/NavigationLink';
import { Toggle as ThemeToggle } from '../../toggle/Toggle';
import { device } from '../../../Theme';
import * as c from '../../../../constants';
import { toast } from 'react-toastify';
import { ReactComponent as LightIcon } from '../../../../img/icons/theme_light_icon.svg';
import { ReactComponent as DarkIcon } from '../../../../img/icons/theme_dark_icon.svg';
import { rgba } from 'polished';
import SimpleBarReact from 'simplebar-react';

export const ProfileDropDown = forwardRef(
  (
    {
      strings,
      open,
      close,
      image,
      profileId,
      profileData,
      show,
      setProfile,
      signOutUser,
      ...props
    },
    ref
  ) => {
    const ProfileDropdown = () => {
      const handleProfSelect = (e, id, name) => {
        e.preventDefault();
        toast.success(`${strings.profileSetSuccess} ${name}`, {
          className: 'data-hj-suppress',
          position: toast.POSITION.BOTTOM_CENTER,
        });

        setProfile(id);
        close();
      };

      return (
        <ClicProfileDropDown ref={ref} open={open}>
          <SimpleBarReact style={{ maxHeight: 'calc(100vh - 108px)' }}>
            {profileData && Object.keys(profileData).length && (
              <UL>
                {Object.keys(profileData).map((key) => {
                  let isActive = false;

                  if (profileId) {
                    if (
                      parseInt(profileId, 10) ===
                      parseInt(profileData[key].id, 10)
                    ) {
                      isActive = true;
                    }
                  } else {
                    if (profileData[key].is_main_profile === 'Y') {
                      isActive = true;
                    }
                  }

                  return (
                    <LI key={key}>
                      <NavigationLink
                        active={isActive}
                        avatar
                        avatarType={
                          profileData
                            ? profileData[key].avatar_filename
                            : 'gorilla'
                        }
                        label={profileData[key].name}
                        size="large"
                        onClick={(e) =>
                          handleProfSelect(
                            e,
                            profileData[key].id,
                            profileData[key].name
                          )
                        }
                      />
                    </LI>
                  );
                })}
              </UL>
            )}
            <UL>
              <LI>
                <NavigationLinkContainer>
                  <NavigationLink
                    label={strings ? strings.addProfile : 'Add a profile'}
                    link={c.PATH_PROFILE_ADD}
                  />
                </NavigationLinkContainer>
              </LI>
              <LI>
                <NavigationLinkContainer>
                  <NavigationLink
                    label={
                      strings ? strings.manageProfiles : 'Manage your profiles'
                    }
                    link={c.PATH_PROFILES}
                  />
                </NavigationLinkContainer>
              </LI>
              <LI>
                <NavigationLinkContainer>
                  <NavigationLink
                    label={
                      strings ? strings.accountSettings : 'Account settings'
                    }
                    link={c.PATH_EDIT}
                  />
                </NavigationLinkContainer>
              </LI>
              <LI>
                <NavigationLinkContainer>
                  <NavigationLink
                    label={strings ? strings.logOut : 'Log out'}
                    onClick={signOutUser}
                  />
                </NavigationLinkContainer>
              </LI>
            </UL>
            <UL>
              <LI>
                <NavigationLabel>
                  {strings ? strings.appearance : 'Appearance'}
                </NavigationLabel>
                <ThemeToggle
                  defaultLabel={strings.light}
                  defaultIcon={<LightIcon />}
                  toggledLabel={strings.dark}
                  toggledIcon={<DarkIcon />}
                />
              </LI>
            </UL>
          </SimpleBarReact>
        </ClicProfileDropDown>
      );
    };
    return <ProfileDropdown />;
  }
);

const LI = styled.li`
  + li {
    margin-top: 0.4em;
  }
`;

const UL = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  &:last-of-type ${LI} {
    display: flex;
    flex-direction: column;
  }
`;

const NavigationLinkContainer = styled.div`
  a {
    height: 32px;
    @media ${device.desktop} {
      padding: 0 25px;
    }
  }
`;

const ClicProfileDropDown = styled.nav`
  padding: 8px 2px 8px 0;
  background: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.whiteFull
      : props.theme.color.blackFull};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'light'
        ? 'rgba(40, 40, 39, 0.15)'
        : 'rgba(243, 243, 243, 0.1)'};
  border-radius: 12px;

  box-shadow: ${(props) =>
    props.theme.name === 'light'
      ? '0px 2px 8px rgba(21, 20, 17, 0.14), 0px 12px 32px -8px rgba(21, 20, 17, 0.12), 0px 24px 64px -16px rgba(21, 20, 17, 0.1)'
      : '0px 2px 8px rgba(21, 20, 17, 0.34), 0px 12px 32px -8px rgba(21, 20, 17, 0.24), 0px 24px 64px -16px rgba(21, 20, 17, 0.16)'};
  overflow: hidden;
  opacity: 0;
  visibility: hidden;
  @supports (backdrop-filter: blur()) {
    background: ${(props) =>
      props.theme.name === 'light'
        ? `${rgba(props.theme.color.whiteFull, 0.7)}`
        : `${rgba(props.theme.color.blackFull, 0.8)}`};
    backdrop-filter: blur(40px);
  }

  ${({ open }) =>
    open &&
    css`
      opacity: 1;
      visibility: visible;
    `};

  ${UL}:not(:last-of-type) {
    padding-bottom: 0.7em;
    &:after {
      display: inline-block;
      margin: 1em 19px 0;
      width: calc(100% - 38px);
      height: 1px;
      background: ${(props) =>
        props.theme.name === 'light'
          ? props.theme.color.black90
          : props.theme.color.white90};
      opacity: 0.1;
      content: '';
    }
  }
`;

const NavigationLabel = styled.span`
  ${(props) => props.theme.fontBook}
  padding: 0 20px;
  font-size: 18px;
  font-weight: 400;
  line-height: 1;
  text-transform: capitalize;
  color: ${(props) =>
    props.theme.name === 'light'
      ? props.theme.color.black90
      : props.theme.color.white90};
`;
